import { FiInfo } from "react-icons/fi";
import { Spinner } from "../../../components/Spinner";
import {
  useGetInsights,
  useGetUserVulnerabilities,
} from "../../../helpers/queries/insights/useGetInsights";
import { formatFullDate } from "../../../lib/utils";
import { useEffect, useMemo, useState } from "react";
import VulnerabilityDetails from "../Report/modals/VulnerabilityDetails";
// import CveModal from "./CveModal";
// import { SeverityChart } from "./Chart";
import BoxChart from "./BoxChart";
import { FiPieChart } from "react-icons/fi";
import CircularScore from "../../../components/CircularScore";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { MdSearch } from "react-icons/md";
import { Button } from "../../../components/ui/button";
import {
  acceptVulnerability,
  exportUserVulnerabilities,
  // getCVEResultsWithStrings,
  resolveVulnerability,
  searchUserVulnerabilities,
} from "../../../helpers/api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import empty from "../../../assets/images/empty_state.png";
import { usePaginationSync } from "../../../hooks/usePaginationSync";
import { PaginationComponent } from "../../../components/Pagination";
import { useLocation } from "react-router-dom";
import { useGetUserRole } from "../../../hooks/useGetUserRole";
import { Check, Minus } from "lucide-react";
import { Checkbox } from "../../../components/ui/checkbox";

const Insights = () => {
  const { isLoading, isSuccess, isError, data } = useGetInsights();
  const [lastPage, setLastPage] = useState(1);
  const { currentPage, updatePage } = usePaginationSync(lastPage);
  const location = useLocation();
  const severityLevel = location?.state?.severityLevel?.toLowerCase() || "";
  const [severity, setSeverity] = useState(severityLevel || "");
  const [status, setStatus] = useState("");

  const {
    isLoading: loadingVuln,
    isSuccess: success,
    isError: error,
    data: vuln,
  } = useGetUserVulnerabilities(currentPage, severity, status);

  useEffect(() => {
    setLastPage(vuln?.meta?.last_page);
  }, [vuln]);

  const [isOpen, setIsOpen] = useState(false);

  const [details, setDetails] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  // const [open, setOpen] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [results, setResults] = useState([]);

  useEffect(() => {
    if (searchTerm && searchResults) {
      setResults(searchResults);
    } else {
      setResults(vuln || []);
    }
  }, [vuln, searchResults, searchTerm]);

  // const handleSearch = async (cve) => {
  //   if (!cve) return;
  //   setLoading(true);
  //   try {
  //     const response = await api.get(`/cve-search/${cve}`);
  //     const result = await response.data;
  //     setResponse(result?.data);
  //     setOpen(true); // Open the dialog on success
  //   } catch (err) {
  //     toast.error(
  //       err?.response?.data?.message?.startsWith("Invalid")
  //         ? "Invalid CVE format"
  //         : err?.response?.data?.message
  //     );
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const { mutate: search, isPending: searchPending } = useMutation({
    mutationFn: searchUserVulnerabilities,
    onSuccess: (data) => {
      if (data) {
        setSearchResults(data);
      } else {
        toast("Uh oh! Something went wrong.");
      }
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message);
    },
  });

  const handleAllSearch = (data) => {
    search({ name: data });
  };

  // const { mutate, isPending } = useMutation({
  //   mutationFn: getCVEResultsWithStrings,
  //   onSuccess: (data) => {
  //     if (data) {
  //       setSearchResults(data);
  //     } else {
  //       toast("Uh oh! Something went wrong.");
  //     }
  //   },
  //   onError: (err) => {
  //     toast.error(err?.response?.data?.message);
  //   },
  // });

  // const handleOtherSearch = (data) => {
  //   mutate({
  //     cve_string: data,
  //   });
  // };

  // const handleClickOutside = () => {
  //   setSearchResults(null);
  // };

  const [loadingExport, setLoadingExport] = useState(false);

  const handleExport = async () => {
    setLoadingExport(true);
    try {
      const blob = await exportUserVulnerabilities(severity);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `${severity ? severity : "all"} vulnerabilities.csv`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Export failed:", error);
    } finally {
      setLoadingExport(false);
    }
  };

  const vulnCount = results?.vulnerabilityCounts;

  const chartData = [
    {
      level: "Total",
      count: vulnCount?.total ?? 0,
      fill: "#828282",
    },
    {
      level: "Critical",
      count: vulnCount?.critical ?? 0,
      fill: "#b22222",
    },
    {
      level: "High",
      count: vulnCount?.high ?? 0,
      fill: "#E3650F",
    },
    {
      level: "Medium",
      count: vulnCount?.medium ?? 0,
      fill: "#c49102",
    },
    {
      level: "Low",
      count: vulnCount?.low ?? 0,
      fill: "#00c851",
    },
  ];

  const { viewer } = useGetUserRole();

  const [selectedIds, setSelectedIds] = useState([]);

  // allIds always reflects current results
  const allIds = useMemo(
    () => vuln?.data?.map((r) => r.id) || [],
    [vuln?.data]
  );

  const isIndeterminate =
    selectedIds.length > 0 && selectedIds.length < allIds.length;
  const isAllSelected = selectedIds.length === allIds.length;

  const toggleSelectAll = () => {
    if (isIndeterminate || isAllSelected) {
      setSelectedIds([]); // clear all
    } else {
      setSelectedIds(allIds); // select all
    }
  };

  const queryClient = useQueryClient();
  const { mutate: accept, isPending: acceptPending } = useMutation({
    mutationFn: (id) => acceptVulnerability(id),
    onSuccess: (data) => {
      toast(data.message);
      queryClient.invalidateQueries({ queryKey: ["user_vuln"] });
    },
    onError: (error) => {
      toast.error(error.message || "Failed to accept vulnerability");
    },
  });

  const { mutate: resolve, isPending: resolvePending } = useMutation({
    mutationFn: (id) => resolveVulnerability(id),
    onSuccess: (data) => {
      toast(data.message);
      queryClient.invalidateQueries({ queryKey: ["user_vuln"] });
    },
    onError: (error) => {
      toast.error(error.message || "Failed to resolve vulnerability");
    },
  });

  return (
    <div className="w-full">
      <div className=" w-full lg:block flex justify-between items-center py-6 lg:px-10 md:px-5 px-4 bg-white border-b ">
        <div className="w-full flex lg:flex-row flex-col justify-between lg:items-center gap-4 mb-1">
          <div className="flex gap-3 items-center">
            <div>
              <div className="w-[50px] h-[50px] rounded-full border border-neutral300 flex justify-center items-center">
                <FiPieChart className="text-neutral700 text-[20px]" />
              </div>
            </div>
            <div>
              <p className="text-darkGrey font-semibold md:block hidden">
                Vulnerability Insights
              </p>
              <p className="text-neutral600 md:text-base text-sm">
                View, track, and prioritize vulnerabilities detected across your
                assets.
              </p>
            </div>
          </div>
        </div>
      </div>

      {isLoading && !isSuccess && (
        <div className="h-[55vh] w-full flex items-center justify-center">
          <Spinner />
        </div>
      )}

      {isError && !isLoading && (
        <div className="h-[45vh] w-full flex justify-center flex-col items-center">
          <p className="font-semibold text-xl text-center">
            There was an error getting the data
          </p>
          <p className="text-center">{data?.message ?? ""}</p>
        </div>
      )}

      {!isLoading && isSuccess && data && (
        <div className="lg:p-10 md:p-5 p-4 bg-white h-[100%]">
          {/* <div className="relative flex items-center md:gap-4 gap-2 mb-4">
            <Select value={type} onValueChange={setType}>
              <SelectTrigger
                className="w-[150px] rounded-lg"
                aria-label="Select a value"
              >
                <SelectValue placeholder="Last 1 month" />
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                <SelectItem value="cve" className="rounded-lg">
                  CVE ID
                </SelectItem>
                <SelectItem value="others" className="rounded-lg">
                  Others
                </SelectItem>
              </SelectContent>
            </Select>
            <div className="md:relative w-full">
              <div className="flex items-center focus-within:ring-1 focus:border-[#60B8D01A] border border-input h-[40px] px-2 rounded-lg w-full">
                <MdSearch color="#B7B7B9" size={24} />
                <input
                  type="text"
                  placeholder={type === "cve" ? "Search for CVE ID" : "Search"}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onClick={handleClickOutside}
                  className="md:px-4 px-2 w-full text-sm py-2 focus:outline-none peer"
                />
              </div>
              {searchResults && (
                <div
                  // ref={dropdownRef}
                  className="absolute min-h-[50px] max-h-[400px] overflow-auto z-10 pb-0 w-full top-12 left-0 bg-white border border-input rounded-lg"
                >
                  {searchResults.length > 0 ? (
                    searchResults.map((item, index) => (
                      <div
                        key={index}
                        className="hover:bg-neutral200 cursor-pointer"
                        onClick={() => handleSearch(item.name)}
                      >
                        <p className="px-4 text-sm text-neutral900 ">
                          {item.name}
                        </p>

                        <p className="px-4 text-xs mb-2 text-neutral-600">
                          {item?.cve?.description}
                        </p>
                      </div>
                    ))
                  ) : (
                    <p className="px-4 py-2 text-sm text-center">No results</p>
                  )}
                </div>
              )}
            </div>

            <Button
              className="h-[40px] transition-opacity"
              loading={(searchTerm && loading) || isPending}
              disabled={!searchTerm}
              onClick={() =>
                type === "cve"
                  ? handleSearch(searchTerm)
                  : handleOtherSearch(searchTerm)
              }
            >
              <span className="md:block md:px-3 hidden">Search</span>
              <span className="md:hidden">
                <MdSearch size={24} />
              </span>
            </Button>
          </div> */}

          <div className=" flex xl:flex-row flex-col gap-8">
            {/* <HexChart data={data?.emerging_threats_summary} /> */}
            <BoxChart
              data={data?.emerging_threats_summary}
              setStatus={setStatus}
            />
            {/* <SeverityChart /> */}
          </div>

          <div className="flex xl:flex-row flex-col xl:justify-between xl:items-center gap-4 mt-8 mb-4">
            <h2 className="font-semibold  text-[#293056]">Vulnerabilities</h2>

            <div className="flex md:flex-row flex-wrap md:items-center gap-4">
              <div className="flex items-center focus-within:ring-1 focus:border-[#60B8D01A] border border-input h-[36px] px-2 rounded-lg w-[200px]">
                <input
                  type="text"
                  placeholder={"Search"}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="md:px-4 px-2 w-full text-sm focus:outline-none peer"
                />

                {searchPending ? (
                  <Spinner />
                ) : (
                  <MdSearch
                    color="black"
                    className="cursor-pointer"
                    size={24}
                    onClick={() => handleAllSearch(searchTerm)}
                  />
                )}
              </div>

              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger
                  className="w-[140px] rounded-lg h-9"
                  aria-label="Status"
                >
                  <SelectValue placeholder="Filter By" />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value="total" className="rounded-lg">
                    Total
                  </SelectItem>
                  <SelectItem value="pending" className="rounded-lg">
                    Unresolved
                  </SelectItem>
                  <SelectItem value="accepted" className="rounded-lg">
                    Accepted
                  </SelectItem>
                  <SelectItem value="resolved" className="rounded-lg">
                    Resolved
                  </SelectItem>
                  <SelectItem value="exploitable" className="rounded-lg">
                    Exploitable
                  </SelectItem>
                  <SelectItem value="high_risk" className="rounded-lg">
                    High Risk
                  </SelectItem>
                </SelectContent>
              </Select>

              <Select value={severity} onValueChange={setSeverity}>
                <SelectTrigger
                  className="w-[120px] rounded-lg h-9"
                  aria-label="Severity"
                >
                  <SelectValue placeholder="Severity" />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value="all" className="rounded-lg">
                    All
                  </SelectItem>
                  <SelectItem value="critical" className="rounded-lg">
                    Critical
                  </SelectItem>
                  <SelectItem value="high" className="rounded-lg">
                    High
                  </SelectItem>
                  <SelectItem value="medium" className="rounded-lg">
                    Medium
                  </SelectItem>
                  <SelectItem value="low" className="rounded-lg">
                    Low
                  </SelectItem>
                </SelectContent>
              </Select>

              <Button
                className="px-3"
                onClick={handleExport}
                loading={loadingExport}
                disabled={vuln?.data?.length === 0}
              >
                Export CSV
              </Button>
            </div>
          </div>

          {loadingVuln && !success && (
            <div className="h-[50vh] w-full flex items-center justify-center">
              <Spinner />
            </div>
          )}

          {error && !loadingVuln && (
            <div className="h-[45vh] w-full flex justify-center flex-col items-center">
              <p className="font-semibold text-xl text-center">
                There was an error getting the data
              </p>
              <p className="text-center">{data?.message ?? ""}</p>
            </div>
          )}

          {!loadingVuln && success && vuln?.data?.length > 0 && (
            <>
              {/* <p className="font-semibold text-darkGrey mb-3">
                Vulnerability Count
              </p> */}
              <div className="flex md:flex-row flex-wrap gap-4 mb-4">
                {chartData.map((entry, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 border-[#cfcfd2] md:px-4 px-2"
                  >
                    <div
                      style={{
                        backgroundColor: entry.fill,
                        width: "20px",
                        height: "20px",
                        borderRadius: "50%",
                      }}
                    ></div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">
                        {entry.level}:
                      </span>
                      <span>{entry.count}</span>
                    </div>
                  </div>
                ))}
              </div>
              {!viewer && (
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    selectedIds.length > 0
                      ? "max-h-20 opacity-100"
                      : "max-h-0 opacity-0"
                  }`}
                >
                  <div className="flex items-center justify-between bg-neutral-100 border rounded-lg px-3 xl:px-4 py-2 mb-2">
                    {/* Checkbox */}
                    <div className=" flex items-center gap-4">
                      <button
                        onClick={toggleSelectAll}
                        className="w-4 h-4 flex items-center justify-center border rounded bg-[#0f172a]"
                      >
                        {isAllSelected ? (
                          <Check size={14} className="text-white" />
                        ) : isIndeterminate ? (
                          <Minus size={14} className="text-white" />
                        ) : null}
                      </button>

                      <div className="flex flex-col gap-1">
                        <span className="text-sm font-medium text-neutral-700">
                          {selectedIds.length}{" "}
                          {selectedIds.length === 1
                            ? "Vulnerability"
                            : "Vulnerabilities"}{" "}
                          selected
                        </span>

                        <Button
                          onClick={() => setSelectedIds(allIds)}
                          className="text-sm cursor-pointer text-blue-500 hover:text-[#0f172a] font-medium"
                          variant={"ghost"}
                          size={"ghost"}
                          disabled={isAllSelected}
                        >
                          Select All
                        </Button>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <Button
                        onClick={() => accept(selectedIds)}
                        className="text-sm cursor-pointer text-red-500 hover:text-red-700 font-medium"
                        variant={"ghost"}
                        size={"ghost"}
                        loading={acceptPending}
                      >
                        Accept risk
                      </Button>
                      <Button
                        onClick={() => resolve(selectedIds)}
                        className="text-sm cursor-pointer text-blue-500 hover:text-blue-700 font-medium"
                        variant={"ghost"}
                        size={"ghost"}
                        loading={resolvePending}
                      >
                        Resolve risk
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              <div className="bg-white relative w-full text-center text-sm text-[#8D9091] flex flex-col rounded-[10px] border border-[#EAECF0] overflow-hidden">
                <div className="overflow-x-auto">
                  <div className="w-full min-w-[1200px] border-neutral300 rounded-lg text-sm">
                    <div className="flex justify-between gap-2 text-left px-3 xl:px-6 py-2 bg-neutral3 rounded-t-lg border-b text-neutral700 font-medium w-full">
                      <div className="w-4 mr-2"></div>
                      <span className="w-[28%]">Vulnerability Name</span>
                      <span className="w-[15%] mr-2">Host</span>
                      <span className="w-[10%]">Severity</span>
                      <span className="w-[10%]">CVSS</span>
                      {/* <span className="w-[10%] text-center">Status</span> */}
                      <span className="flex-1">Discovered</span>
                      <span className="flex-1">Last Scanned</span>
                      <span className="w-[5%] text-center"></span>
                    </div>

                    {results?.data?.map((item, index) => {
                      const isChecked = selectedIds.includes(item?.id);
                      return (
                        <section
                          key={index}
                          className={`min-h-[60px] py-2 px-3 xl:px-6 flex text-neutral900 items-center gap-2 ${
                            index === 0
                              ? "border-t-0"
                              : "border-t border-t-[#EAECF0]"
                          } hover:bg-[#F9FAFB]`}
                        >
                          <div className="flex items-center justify-center w-4 mr-2">
                            <Checkbox
                              checked={isChecked}
                              onCheckedChange={() => {
                                setSelectedIds((prev) =>
                                  prev.includes(item.id)
                                    ? prev.filter((id) => id !== item.id)
                                    : [...prev, item.id]
                                );
                              }}
                            />
                          </div>
                          <div className="w-[28%] text-left text-neutral900 truncate overflow-hidden text-nowrap font-medium">
                            {item?.vulnerability_name}
                          </div>
                          <div className="w-[15%] text-left mr-2 truncate">
                            <p>{item?.processed_host}</p>
                          </div>
                          <div className="w-[10%]">
                            {item?.threat_level === "Low" ||
                            item?.threat_level === "Log" ? (
                              <p className="bg-success10 text-success100 border-[1.5px] rounded-full border-[#00C8511A] w-fit px-2 py-1">
                                {item?.threat_level}
                              </p>
                            ) : item?.threat_level === "Medium" ? (
                              <p className="border-cream100 text-brown100 border-[1.5px] rounded-full bg-[#FFBB331A] w-fit px-2 py-1">
                                Medium
                              </p>
                            ) : item?.threat_level === "High" ? (
                              <p className="border-[#F973161A] text-[#E3650F] border-[1.5px] rounded-full bg-[#F973161A] w-fit px-2 py-1">
                                High
                              </p>
                            ) : item?.threat_level === "Critical" ? (
                              <p className="bg-[#FF44441A] text-[#E12A5B] border-[1.5px] rounded-full border-[#FFC1C1] w-fit px-2 py-1">
                                Critical
                              </p>
                            ) : null}
                          </div>
                          <div className="w-[10%]">
                            <CircularScore score={item?.severity} />
                          </div>
                          {/* <div className="w-[10%] text-center">
                            {item?.status === "pending" ? (
                              <p className="border-cream100 text-brown100 border-[1.5px] rounded-full bg-[#FFBB331A] w-fit px-2 py-1 mx-auto ">
                                Pending
                              </p>
                            ) : item?.status === "resolved" ? (
                              <p className="border-[#d3e8f1] text-[#0BA5EC] border-[1.5px] rounded-full bg-[#F1F8FF] w-fit px-2 py-1 mx-auto ">
                                Resolved
                              </p>
                            ) : (
                              <p className="bg-[#FF44441A] text-[#E12A5B] border-[1.5px] rounded-full border-[#FFC1C1] w-fit px-2 py-1 mx-auto capitalize">
                                {item?.status}
                              </p>
                            )}
                          </div> */}
                          <div className="flex-1 text-left">
                            <span className="text-neutral800">
                              {formatFullDate(item?.discovered_at)}
                            </span>
                          </div>
                          <div className="flex-1 text-left text-grey">
                            <span className="text-neutral800">
                              {formatFullDate(item?.target_last_scanned_at)}
                            </span>
                          </div>

                          <div className="w-[5%]  flex text-center justify-center">
                            <FiInfo
                              className="text-blue-600 cursor-pointer"
                              onClick={() => {
                                setDetails(item);
                                setIsOpen(true);
                              }}
                            />
                          </div>
                        </section>
                      );
                    })}
                  </div>
                </div>
              </div>
              <PaginationComponent
                lastPage={results?.meta?.last_page}
                currentPage={currentPage}
                handlePageChange={updatePage}
              />
            </>
          )}

          {vuln?.data?.length === 0 && !loadingVuln && success && (
            <div className="h-[45vh] w-full flex justify-center flex-col items-center text-center p-4">
              <img src={empty} alt="empty" />
              <p className=" font-semibold mt-4 text-darkGrey">
                No vulnerabilities found yet
              </p>
              <p className="text-neutral600 text-sm max-w-[360px] my-3">
                Add and scan multiple assets to get more insights.
              </p>
            </div>
          )}

          <VulnerabilityDetails
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            details={details}
          />

          {/* <CveModal isOpen={open} setIsOpen={setOpen} data={response} /> */}
        </div>
      )}

      {!data && !isLoading && isSuccess && (
        <div className="h-[45vh] mt-[6rem] w-full flex justify-center flex-col items-center text-center p-4">
          <img src={empty} alt="empty" />
          <p className=" font-semibold mt-4 text-darkGrey">
            No insights added yet
          </p>
          <p className="text-neutral600 text-sm max-w-[360px] my-3">
            Add and scan multiple assets to get accurate insights.
          </p>
        </div>
      )}
    </div>
  );
};

export default Insights;
